import React from 'react';
import { DeviceAttributeName } from 'dto/device-dto';
import { DeviceInfosStore } from 'store/device-details/device-info-store';
import { notification, showConfirmDialog, showInformationDialog } from 'ui-lib';
import { useDevicePermissionContext } from 'contexts/permissions/device-permission.context';

interface IEditableDetailComponent {
    deviceInfosStore: DeviceInfosStore;
    attributeName: DeviceAttributeName;
    initialAttributeValue?: string;
    beforeUpdate?: (value: string) => void;
    onDestroy?: () => void;
    afterFinish?: (updated: boolean, value?: string) => void;
}

export interface IEditableActionProps {
    editing: boolean;
    hasPermission: boolean;
    attributeValue: string;
    toggleEdit: () => void;
    onSaveAttribute: () => void;
    onUpdateAttribute: (value: string) => void;
}
export const useInteractiveAttribute = (props: IEditableDetailComponent) => {
    const { deviceInfosStore, attributeName, initialAttributeValue, onDestroy } = props;
    const [editing, setEditing] = React.useState<boolean>(false);
    const [attributeValue, setAttributeValue] = React.useState<string>(initialAttributeValue);
    const {runHandler, hasAttributePermission} = useDevicePermissionContext();
    const hasPermission = React.useMemo(() => hasAttributePermission(attributeName), [attributeName]);

    React.useEffect(() => {
        setAttributeValue(initialAttributeValue);
    }, [initialAttributeValue]);

    React.useEffect(() => {
        if (!editing) return;
        if (!hasPermission) {
            setEditing(false);
            return;
        }
        return onDestroy;
    }, [editing, hasPermission]);

    const onOk = async (value: string) => {
        if (props.attributeName === 'timezone' && value == '') {
            setAttributeValue(initialAttributeValue);
            setEditing(false);
            return showInformationDialog({
                modalType: 'error',
                title: 'Error',
                content: 'Cannot set an empty timezone.',
            });
        }

        const result = await deviceInfosStore.editAttribute(attributeName, value);
        props.afterFinish && props.afterFinish(result.updated, value);
        if (result.updated) return setEditing(false);
        notification.error({
            message: 'Error',
            description: result.reason,
        });
    };

    const onCancel = () => {
        setAttributeValue(initialAttributeValue);
        setEditing(false);
    };

    const onSaveAttribute = (value?: string) => {
        if (!editing) {
            notification.warning({
                message: 'Warning',
                description: 'Can\'t save the value while not in edit mode.',
            });
            return;
        }
        if (!deviceInfosStore.hasValueChange(attributeName, value)) return setEditing(false);

        props.beforeUpdate && props.beforeUpdate(value);

        showConfirmDialog({
            title: 'Confirmation',
            content: 'Do you want to save your change?',
            okText: 'Save',
            onOk: () => onOk(value),
            onCancel,
        });
    };

    return {
        editing,
        attributeValue,
        hasPermission,
        toggleEdit: () => setEditing(!editing),
        onUpdateAttribute: (value: string) => {
            runHandler(attributeName, async () => {
                if (!editing) {
                    notification.warning({
                        message: 'Warning',
                        description: 'Can\'t change the value while not in edit mode.',
                    });
                    return;
                }
                setAttributeValue(value);
            });
        },
        onSaveAttribute: (value: string) => {
            runHandler(attributeName, async () => {
                onSaveAttribute(value);
            });
        },
    };
};